// hooks
import { type MutationParams, Pagination, useCommonMutation, useCommonQuery } from 'hooks';

// modules
import type { BaseApiResponse, CommonQueryOptions, ResultError } from 'modules';

// apis
import { EntryAPI } from '../apis/entry.api';

// keys
import { entryKeys } from '../keys/entry.key';

import type {
  EntryDetailDto,
  EntryHistoryItem,
  EntryHistoryResDto,
  EntryListDto,
  EntryMoCountDtoGroup,
  EntryTypeDto,
  EntryWeeklyDtoEntryWeeklyResDto,
  ReqAttendanceCount,
  ReqEntryBatchInsert,
  ReqEntryHistorySearch,
  ReqEntrySearch,
  ResEntryStatistics
} from '../models/entry.model';

// libraries
import { type UseMutationResult } from '@tanstack/react-query';

export const entryApi = new EntryAPI();

/** 출역 이력 페이지 조회 쿼리 훅 */
export const useGetEntryPage = ({
  data,
  options
}: {
  data: ReqEntrySearch;
  options?: CommonQueryOptions<
    BaseApiResponse<EntryMoCountDtoGroup, EntryListDto>,
    ResultError,
    { list: EntryListDto[]; object: EntryMoCountDtoGroup }
  >;
}) => {
  const queryKeyFactory = (data: ReqEntrySearch) => entryKeys.GET_ENTRY_PAGE(data);

  const queryFn = async (data: ReqEntrySearch, pagination: Pagination) => {
    const page = {
      page: pagination.current,
      size: pagination.pageSize
    };

    return await entryApi.getEntryPage({
      ...data,
      ...page
    });
  };

  return useCommonQuery<
    ReqEntrySearch,
    BaseApiResponse<EntryMoCountDtoGroup, EntryListDto>,
    ResultError,
    { list: EntryListDto[]; object: EntryMoCountDtoGroup }
  >({
    data,
    options: {
      ...options,
      select: (data) => {
        return {
          list: data.resultList,
          object: data.resultObject
        };
      }
    },
    queryKeyFactory,
    queryFn
  });
};

/** 출입 엔트리 배치 등록 쿼리 훅 */
export const useEnrollBatchEntry = (): UseMutationResult<
  BaseApiResponse<EntryDetailDto>,
  ResultError,
  MutationParams<BaseApiResponse<EntryDetailDto>, ReqEntryBatchInsert>,
  unknown
> => {
  return useCommonMutation<BaseApiResponse<EntryDetailDto>, ResultError, ReqEntryBatchInsert>({
    mutationKey: entryKeys.ENROLL_BATCH_ENTRY(),
    mutationFn: async (data) => await entryApi.enrollBatchEntry(data),
    queryKeyToInvalidate: entryKeys.ENROLL_BATCH_ENTRY()
  });
};

/** 출입 엔트리 관리 쿼리 훅 */
export const useManageEntry = (): UseMutationResult<
  BaseApiResponse<EntryDetailDto>,
  ResultError,
  MutationParams<BaseApiResponse<EntryDetailDto>, { mac: string; params: EntryTypeDto }>,
  unknown
> => {
  return useCommonMutation<
    BaseApiResponse<EntryDetailDto>,
    ResultError,
    { mac: string; params: EntryTypeDto }
  >({
    mutationKey: entryKeys.MANAGE_ENTRY(),
    mutationFn: async (data) => await entryApi.manageEntry(data.mac, data.params),
    queryKeyToInvalidate: entryKeys.MANAGE_ENTRY()
  });
};

/** 출입 인원 수 조회 쿼리 훅 */
export const useGetAttendanceCount = ({
  data,
  options
}: {
  data: ReqAttendanceCount;
  options?: CommonQueryOptions<
    BaseApiResponse<unknown, ResEntryStatistics>,
    ResultError,
    ResEntryStatistics[]
  >;
}) => {
  const queryKeyFactory = (data: ReqAttendanceCount) => entryKeys.GET_ATTENDANCE_COUNT(data);

  const queryFn = async (data: ReqAttendanceCount) => {
    return await entryApi.getAttendanceCount(data);
  };

  return useCommonQuery<
    ReqEntrySearch,
    BaseApiResponse<unknown, ResEntryStatistics>,
    ResultError,
    ResEntryStatistics[]
  >({
    data,
    options: {
      ...options,
      select: (data) => data.resultList
    },
    queryKeyFactory,
    queryFn
  });
};

/** 주간 총 합계 조회 쿼리 훅 */
export const useGetTotalCountWeekly = ({
  data,
  options
}: {
  data: string;
  options?: CommonQueryOptions<
    BaseApiResponse<unknown, EntryWeeklyDtoEntryWeeklyResDto>,
    ResultError,
    EntryWeeklyDtoEntryWeeklyResDto[]
  >;
}) => {
  const queryKeyFactory = (data: string) => entryKeys.GET_TOTAL_COUNT_WEEKLY(data);

  const queryFn = async (data: string) => {
    return await entryApi.getTotalCountWeekly(data);
  };

  return useCommonQuery<
    string,
    BaseApiResponse<unknown, EntryWeeklyDtoEntryWeeklyResDto>,
    ResultError,
    EntryWeeklyDtoEntryWeeklyResDto[]
  >({
    data,
    options: {
      ...options,
      select: (data) => {
        return data.resultList;
      }
    },
    queryKeyFactory,
    queryFn
  });
};

/** 사용자 개인 출역 이력 조회 쿼리 훅 */
export const useGetEntryHistory = ({
  data,
  options
}: {
  data: {
    userId: string;
    params: Omit<ReqEntryHistorySearch, 'userId'>;
  };
  options?: CommonQueryOptions<
    BaseApiResponse<EntryHistoryResDto, EntryHistoryItem>,
    ResultError,
    { list: EntryHistoryItem[]; object: EntryHistoryResDto }
  >;
}) => {
  const queryKeyFactory = (data: {
    userId: string;
    params: Omit<ReqEntryHistorySearch, 'userId'>;
  }) => entryKeys.GET_ENTRY_HISTORY(data.userId, data.params);

  const queryFn = (
    data: {
      userId: string;
      params: Omit<ReqEntryHistorySearch, 'userId'>;
    },
    pagination: Pagination
  ) => {
    const page = {
      page: pagination.current,
      size: pagination.pageSize
    };

    return entryApi.getEntryHistory(data.userId, { ...data.params, ...page });
  };

  return useCommonQuery<
    {
      userId: string;
      params: Omit<ReqEntryHistorySearch, 'userId'>;
    },
    BaseApiResponse<EntryHistoryResDto, EntryHistoryItem>,
    ResultError,
    { list: EntryHistoryItem[]; object: EntryHistoryResDto }
  >({
    data,
    options: {
      ...options,
      select: (data) => ({ list: data.resultList, object: data.resultObject })
    },
    queryKeyFactory,
    queryFn
  });
};

/** 작업자 ID를 통한 출입 목록 조회 쿼리 훅 */
export const useGetEntryListByWorkerId = ({
  data,
  options
}: {
  data: {
    userId: string;
    params: ReqEntrySearch;
  };
  options?: CommonQueryOptions<BaseApiResponse<unknown, EntryListDto>, ResultError, EntryListDto[]>;
}) => {
  const queryKeyFactory = (data: { userId: string; params: ReqEntrySearch }) =>
    entryKeys.GET_ENTRY_LIST_BY_WORKER_ID(data.userId, data.params);

  const queryFn = async (data: { userId: string; params: ReqEntrySearch }) => {
    return await entryApi.getEntryListByWorkerId(data.userId, data.params);
  };

  return useCommonQuery<
    {
      userId: string;
      params: ReqEntrySearch;
    },
    BaseApiResponse<unknown, EntryListDto>,
    ResultError,
    EntryListDto[]
  >({
    data,
    options: {
      ...options,
      select: (data) => data.resultList
    },
    queryKeyFactory,
    queryFn
  });
};
